/**
 * Step 3 server sections for WorkWise: the Deployment Model choice (in the
 * CI/CD radio style), App (always dedicated), Database and Gateway. Revision 3
 * links the DB and the gateway: SaaS-Hybrid = both shared, Dedicated = both
 * dedicated, so the two sections follow the one model. A dedicated DB and
 * gateway take the step-2 size, shown read-only like the App server's (R3-G); the
 * gateway gets the App server's "Data Disks" list, the DB the SQL Server image
 * (read-only), the SQL authentication and the "SQL Server storage" panel (see
 * DbStorageFields). Revision 6: the App server has no
 * admin credential input, only a note on the generated admin passwords.
 * Revision 8: the gateway has no Host Name or TLS Certificate input in either
 * model. Same classes as the rest of the page.
 */
import { Server, Share2 } from 'lucide-react'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  DEPLOYMENT_MODEL_OPTIONS,
  DISK_TYPES,
  OS_OPTIONS,
  WORKWISE_ADMIN_USERNAME,
  ZONE_OPTIONS,
} from '@/config/workwise'
import type { WorkwiseFlow } from '@/hooks/useWorkwiseFlow'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { WorkwiseFormValues } from '@/types/workwise'
import { SqlAuthenticationFields, SqlImageField, SqlStoragePanel } from './DbStorageFields'
import { InfoField, RadioField, Section, SelectField, ToggleField } from './fields'
import { DataDisksField, NsgRulesField, SharedServerSelect, VmNameField } from './ServerFields'

const DISK_OPTIONS = DISK_TYPES.map((t) => ({ value: t, label: t }))
const MODEL_ICONS = { 'saas-hybrid': <Share2 size={16} />, dedicated: <Server size={16} /> }
const MODEL_CHOICES = DEPLOYMENT_MODEL_OPTIONS.map((o) => ({ value: o.value, label: `${o.label} (${o.summary})`, icon: MODEL_ICONS[o.value] }))
const ADMIN_NOTE = `Admin user: ${WORKWISE_ADMIN_USERNAME}. A password is generated for each VM during deployment and stored in Key Vault as <vm name>-admin-password.`

/** One choice for the DB and the gateway together; placed under CI/CD, in the same look. */
export function DeploymentModelField() {
  const { control, clearErrors } = useFormContext<WorkwiseFormValues>()
  const model = useWatch({ control, name: 'deploymentModel' })
  const hint =
    model === 'dedicated'
      ? 'Dedicated: the customer gets its own database and gateway servers at the size chosen in Configure Sizes, so three VMs are created, in its own resource group, VNet and subnet with the address ranges entered under Network.'
      : 'SaaS-Hybrid: the customer shares a database server and a gateway server, so only the app server is created (one VM). The network comes from the shared database server, or from an existing network chosen under Network.'
  return (
    <RadioField
      path="deploymentModel"
      label="Deployment Model"
      testIdPrefix="deployment-model"
      choices={MODEL_CHOICES}
      hint={hint}
      onChange={() => clearErrors(['dbServer', 'gatewayServer', 'network'])}
    />
  )
}

/** The size chosen in Configure Sizes, read-only: the App server's, and a dedicated DB's / gateway's too (R3-G). */
function StepTwoSizeField({ flow, testId }: { flow: WorkwiseFlow; testId: string }) {
  const { control } = useFormContext<WorkwiseFormValues>()
  const sku = useWatch({ control, name: 'appServer.sku' })
  const vmSize = flow.sizeInfo.vmSizeFor(sku)
  return <InfoField label="Size" value={vmSize ? `${sku} · ${vmSize}` : sku} hint="Chosen in Configure Sizes" testId={testId} />
}

export function AppServerSection({ flow }: { flow: WorkwiseFlow }) {
  return (
    <Section title="App Server" badge="Dedicated" testId="ww-app">
      <div className={styles.formGrid}>
        <StepTwoSizeField flow={flow} testId="ww-app-size" />
        <VmNameField flow={flow} path="appServer.vmName" label="VM Name" hint="<e>ww<nnn>app<NN>: p = production, d = dev/staging; digits = customer number" />
        <SelectField path="appServer.os" label="Operating System" options={OS_OPTIONS} required />
        <SelectField path="appServer.osDiskType" label="OS Disk Type" options={DISK_OPTIONS} />
        <SelectField path="appServer.zone" label="Availability Zone" options={ZONE_OPTIONS} />
      </div>
      <DataDisksField />
      <div className={styles.fieldHint} style={{ marginTop: 16 }} data-testid="ww-app-admin">
        {ADMIN_NOTE}
      </div>
    </Section>
  )
}

export function DbServerSection({ flow }: { flow: WorkwiseFlow }) {
  const { control } = useFormContext<WorkwiseFormValues>()
  const model = useWatch({ control, name: 'deploymentModel' })
  const shared = model !== 'dedicated'
  return (
    <Section title="Database Server" badge={shared ? 'Shared' : 'Dedicated'} testId="ww-db">
      {shared ? (
        <div className={styles.formGrid}>
          <SharedServerSelect role="db" path="dbServer.sharedServerId" label="Shared Database Server" />
        </div>
      ) : (
        <>
          <div className={styles.formGrid}>
            <StepTwoSizeField flow={flow} testId="ww-db-size" />
            <VmNameField flow={flow} path="dbServer.vmName" label="VM Name" hint="<e>ww<nnn>db<NN>" />
            <SqlImageField />
            <SelectField path="dbServer.zone" label="Availability Zone" options={ZONE_OPTIONS} />
            <SqlAuthenticationFields />
          </div>
          <SqlStoragePanel />
        </>
      )}
    </Section>
  )
}

export function GatewayServerSection({ flow }: { flow: WorkwiseFlow }) {
  const { control } = useFormContext<WorkwiseFormValues>()
  const [model, environment] = useWatch({ control, name: ['deploymentModel', 'environment'] })
  const shared = model !== 'dedicated'
  const gatewayNames = flow.vmNames?.gateway
  const exhausted = !shared && gatewayNames !== undefined && gatewayNames.next === null
  return (
    <Section title="Gateway Server" badge={shared ? 'Shared' : 'Dedicated'} testId="ww-gateway">
      {shared ? (
        <div className={styles.formGrid}>
          <SharedServerSelect role="gateway" path="gatewayServer.sharedServerId" label="Shared Gateway Server" />
        </div>
      ) : (
        <>
          {exhausted && (
            <div className={styles.lbInfoBanner} style={{ marginTop: 0, marginBottom: 16 }}>
              All nine <strong>{environment === 'production' ? 'p' : 'd'}mtwwt0x</strong> gateway names are in use; a tenth has no
              defined name yet. Use the SaaS-Hybrid model (shared gateway) instead.
            </div>
          )}
          <div className={styles.formGrid}>
            <StepTwoSizeField flow={flow} testId="ww-gateway-size" />
            <VmNameField
              flow={flow}
              path="gatewayServer.vmName"
              label="VM Name"
              hint={gatewayNames?.used.length ? `<e>mtwwt0<x>, next free suggested. In use: ${gatewayNames.used.join(', ')}` : '<e>mtwwt0<x>, x = 1–9'}
            />
            <SelectField path="gatewayServer.zone" label="Availability Zone" options={ZONE_OPTIONS} />
          </div>
          <div style={{ marginTop: 16 }}>
            <ToggleField path="gatewayServer.publicIp" label="Public IP" desc="Attach a static public IP address to the gateway" />
          </div>
          <DataDisksField path="gatewayServer.disks" />
          <NsgRulesField />
        </>
      )}
    </Section>
  )
}
